import uid from "common/utils/uid";
import { polyLine2EdgeCount } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { range } from "geom/utils";
import { defaultStairConfig } from "../../elements/stairSystem/stairConstants";
import type { DocMigration } from "../docMigration";
import { addParamConfig } from "../docMigrationUtils";

const DEFAULT_WINDOW_HEAD_HEIGHT = 96;

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_3: DocMigration = {
  maxSchemaVersion: "0.0.3",
  apply(json: any) {
    if (!json.referenceGeom) {
      json.referenceGeom = {};
    }

    for (const geomId in json.referenceGeom) {
      const geometry = json.referenceGeom[geomId];

      if (geometry.type !== "POLYLINE2") {
        continue;
      }

      geometry.geom = polyLine2New(geometry.geom);

      if (!geometry.edgeIds) {
        const edgeCount = polyLine2EdgeCount(geometry.geom);
        geometry.edgeIds = range(edgeCount).map((_, i) => i.toString());
      }
    }

    if (json.elevations === undefined) {
      const defaultElevationId = uid();
      json.elevations = Object.freeze({
        [defaultElevationId]: defaultElevation({ id: defaultElevationId }),
      });
    }

    if (json.levels && json.levels.length) {
      for (const level of json.levels) {
        if (!level.windowHeadHeight) {
          level.windowHeadHeight = DEFAULT_WINDOW_HEAD_HEIGHT;
        }
      }
    }

    if (json.stairSystems) {
      for (const stairId in json.stairSystems) {
        const stair = json.stairSystems[stairId];
        if (!stair.config) {
          stair.config = defaultStairConfig;
        }
      }
    }

    if (json.roofSystems) {
      for (const roofId in json.roofSystems) {
        const roof = json.roofSystems[roofId];

        if (roof.type !== "MULTI_GABLE") {
          continue;
        }

        // collect all the roof names
        const roofNameExists: any = {};
        for (const subRoof of roof.subRoofs) {
          if (subRoof.name !== undefined && subRoof.name !== "" && subRoof.name !== null) {
            roofNameExists[subRoof.name] = true;
          }
        }

        // create a roof name for any roofs with undefined names
        let i = 0;
        for (const subRoof of roof.subRoofs) {
          if (subRoof.name === undefined) {
            let name = `roof-${i}`;
            while (roofNameExists[name]) {
              i++;
              name = `roof-${i}`;
            }
            subRoof.name = name;
            i++;
          }
        }
      }
    }

    for (const paramId in json.params) {
      const param = json.params[paramId] as any;
      addParamConfig(paramId, param);
    }

    return json;
  },
};

const defaultElevation = (params = {}): any => ({
  id: uid(),
  entityType: "ELEVATION",
  sortIndex: 0,
  name: "default-elevation",
  isDefault: true,
  prettyName: "Default elevation",
  baseSettings: "Elliott_H00",
  defaultExteriorCamera: {
    x: 185,
    y: -940,
    z: 65,
    eyeLevel: 120,
    maxPolarAngle: 1.57,
  },
  ...params,
});

export default migration_0_0_3;
